import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, useAnimation, useMotionValue } from 'framer-motion';
import useTranslation from '../hooks/useTranslation';
import './Clients.css';

const CLIENT_LOGOS = [
  { name: 'Institut du Monde Arabe', src: '/images/logos/ima.jpeg' },
  { name: 'London Public Library', src: '/images/logos/lpl.jpeg' },
  { name: 'The British Museum', src: '/images/logos/bm.webp' },
  { name: 'Sheikh Abdullah Al Salem Cultural Centre', src: '/images/logos/ascc.jpeg' },
  { name: 'Higher Institute of Dramatics Arts', src: '/images/logos/hida.jpeg' },
  { name: 'College of Basic Education', src: '/images/logos/cbe.jpeg' },
  { name: 'Kuwait', src: '/images/logos/kuwait.jpeg' },
  { name: 'Kuwait Research and Studies Center', src: '/images/logos/krsc.jpeg' },
  { name: 'Bait Alhikma', src: '/images/logos/bait_alhikma.jpeg' },
  { name: 'NBN', src: '/images/logos/nbn.jpeg' },
  { name: 'Tdaber', src: '/images/logos/tad.jpeg' },
  { name: 'Amer', src: '/images/logos/amer.jpeg' },
];

const Clients = () => {
  const { t, isRTL } = useTranslation();
  const trackRef = useRef(null);
  const controls = useAnimation();
  const x = useMotionValue(0);
  const [isDragging, setIsDragging] = useState(false);

  // Triple the list to ensure seamless infinite scroll during dragging
  const tripledLogos = [...CLIENT_LOGOS, ...CLIENT_LOGOS, ...CLIENT_LOGOS];

  const startAnimation = useCallback(async () => {
    const singleSetWidth = trackRef.current ? trackRef.current.scrollWidth / 3 : 2250;

    const direction = isRTL ? 1 : -1;
    const targetX = direction * singleSetWidth;

    await controls.start({
      x: [x.get(), targetX],
      transition: {
        duration: 40 * (Math.abs(targetX - x.get()) / singleSetWidth),
        ease: "linear",
        repeat: Infinity,
        repeatType: "loop",
        repeatDelay: 0
      }
    });
  }, [controls, isRTL, x]);

  useEffect(() => {
    if (!isDragging) {
      startAnimation();
    }
  }, [isDragging, startAnimation]);

  // Handle Wrap-around for infinite feel
  useEffect(() => {
    const unsubscribe = x.on("change", (latest) => {
      if (!trackRef.current) return;
      const singleSetWidth = trackRef.current.scrollWidth / 3;

      if (isRTL) {
        if (latest >= singleSetWidth) {
          x.set(0);
        } else if (latest <= -singleSetWidth) {
          x.set(0);
        }
      } else {
        if (latest <= -singleSetWidth) {
          x.set(0);
        } else if (latest >= singleSetWidth) {
          x.set(0);
        }
      }
    });
    return () => unsubscribe();
  }, [isRTL, x]);

  return (
    <section className="clients" id="clients">
      <div className="container">
        <h2 className="clients__title">{t('clients.title')}</h2>
      </div>

      <div className="clients__slider">
        <motion.div
          ref={trackRef}
          className="clients__track"
          style={{ x }}
          animate={controls}
          drag="x"
          onDragStart={() => {
            setIsDragging(true);
            controls.stop();
          }}
          onDragEnd={() => {
            setIsDragging(false);
          }}
          // Constraints to prevent dragging too far away from the middle set
          dragConstraints={{ left: -5000, right: 5000 }}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {tripledLogos.map((logo, index) => (
            <div key={`${logo.name}-${index}`} className="clients__logo-container">
              <img
                src={logo.src}
                alt={logo.name}
                className="clients__logo"
                loading="lazy"
                draggable="false"
              />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Clients;
